/**
 * Vacation Request Form Page
 * Route: /vacation/request
 * Story: VAC-7, VAC-DS-04
 */

'use client'

import React, { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Home, Calendar, AlertCircle, CheckCircle } from 'lucide-react'
import ProtectedRoute from '@/components/ProtectedRoute'
import { VacationRequestForm } from '@/components/VacationRequestForm'
import { useVacationBalance } from '@/hooks/useVacationBalance'
import { useCreateVacationRequest } from '@/hooks/useVacationRequests'
import type { VacationRequest, CreateVacationRequestInput } from '@/types/vacation'

function VacationRequestPageContent() {
  const navigate = useNavigate()
  const { data: balance, isLoading: isLoadingBalance, error: balanceError } = useVacationBalance()
  const { createRequest } = useCreateVacationRequest()

  const [successMessage, setSuccessMessage] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleSuccess = (request: VacationRequest) => {
    setSuccessMessage(
      `Solicitação ${request.requestId} criada com sucesso! Redirecionando...`
    )

    // Redirect to requests list after 2 seconds
    setTimeout(() => {
      navigate({ to: '/vacation/requests' })
    }, 2000)
  }

  const handleError = (error: Error) => {
    setErrorMessage(error.message || 'Erro ao criar solicitação. Tente novamente.')
  }

  const handleSubmit = async (input: CreateVacationRequestInput): Promise<VacationRequest> => {
    return await createRequest(input)
  }

  if (isLoadingBalance) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-brand-primary border-t-transparent mb-4"></div>
        <p className="text-brand-gray font-body">Carregando saldo de férias...</p>
      </div>
    )
  }

  if (balanceError || !balance) {
    return (
      <div className="bg-error/10 border border-error/30 rounded-monumenta p-4 mb-6">
        <div className="flex items-center space-x-3">
          <AlertCircle className="text-error flex-shrink-0" size={20} />
          <div className="flex-1">
            <h4 className="font-display font-medium text-error mb-1">
              Erro ao carregar saldo de férias
            </h4>
            <p className="text-sm text-brand-gray">
              {balanceError?.message || 'Nenhum saldo encontrado.'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Use the balance directly (single object, not array)
  const currentBalance = balance

  return (
    <div className="min-h-screen bg-brand-dark text-white">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Breadcrumb */}
        <nav className="text-sm text-brand-gray mb-6">
          <a href="/dashboard" className="hover:text-white transition-colors inline-flex items-center gap-1">
            <Home size={14} />
            Dashboard
          </a>
          <span className="mx-2">›</span>
          <a href="/vacation" className="hover:text-white transition-colors inline-flex items-center gap-1">
            <Calendar size={14} />
            Férias
          </a>
          <span className="mx-2">›</span>
          <span className="text-white font-medium">Solicitar Férias</span>
        </nav>

        {/* Page header */}
        <h1 className="text-3xl font-display font-bold text-brand-primary mb-8">
          Solicitar Férias
        </h1>

        {/* Vacation Request Form */}
        <VacationRequestForm
          balance={currentBalance}
          onSuccess={handleSuccess}
          onError={handleError}
          onSubmit={handleSubmit}
        />

        {/* Success Notification */}
        {successMessage && (
          <div className="fixed top-4 right-4 z-50 animate-fade-in">
            <div className="bg-success/10 border border-success/30 rounded-monumenta p-4 shadow-monumenta max-w-md">
              <div className="flex items-center space-x-3">
                <CheckCircle className="text-success flex-shrink-0" size={20} />
                <div className="flex-1">
                  <p className="text-white font-display">{successMessage}</p>
                  <p className="text-sm text-brand-gray mt-1">Redirecionando...</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Notification */}
        {errorMessage && (
          <div className="fixed top-4 right-4 z-50 animate-fade-in">
            <div className="bg-error/10 border border-error/30 rounded-monumenta p-4 shadow-monumenta max-w-md">
              <div className="flex items-center space-x-3">
                <AlertCircle className="text-error flex-shrink-0" size={20} />
                <div className="flex-1">
                  <p className="text-white font-display">{errorMessage}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default function VacationRequestPage() {
  return (
    <ProtectedRoute>
      
        <VacationRequestPageContent />
      
    </ProtectedRoute>
  )
}
